//@author: travis

import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    viewChild,
    OnInit,
    ViewEncapsulation,
} from "@angular/core";
import { DropDownItem } from "../../../pojo/DropDownItem";
import { Task } from "../../../pojo/Task";
import { NotificationPolicyService } from "../../../services/notification.policy.service";
import { S25Const } from "../../../util/s25-const";
import { NotificationPolicyModel } from "../../s25-notification-policy/notification.policy.model";
import { S25BulkEditSaveApi } from "../save/s25.bulk.edit.save.api";
import { NotificationPolicyMapper } from "../../s25-notification-policy/notification.policy.mapper";
import { S25NotificationPolicyComponent } from "../../s25-notification-policy/s25.notification.policy.component";
import { S25Util } from "../../../util/s25-util";
import { TypeManagerDecorator } from "../../../main/type.map.service";

@TypeManagerDecorator("s25-ng-bulk-edit-notify-policy")
@Component({
    selector: "s25-ng-bulk-edit-notify-policy",
    template: `@if (init) {
        <div>
            <span class="c-margin-top--half c-margin-right--half ngInlineBlock ngBold">Update Notify Type Only</span>
            <s25-toggle-button
                (valueChange)="onNotifyTypeChange($event)"
                [value]="notifyTypeOnly"
                class="ngBlock c-margin-bottom--single"
            ></s25-toggle-button>
            @if (notifyTypeOnly) {
                <div>
                    <s25-generic-dropdown
                        [items]="notifyTypes"
                        [searchEnabled]="false"
                        (valueChange)="setNotifyType($event, i)"
                    />
                </div>
            } @else {
                <div>
                    <span class="c-margin-right--half ngInlineBlock ngBold">Update Notification Policy By</span>
                    <s25-toggle-button
                        (valueChange)="onTypeChange($event)"
                        [value]="contactsOnly"
                        [trueLabel]="'Contacts'"
                        [falseLabel]="'Template'"
                        class="ngBlock c-margin-bottom--single"
                    ></s25-toggle-button>
                    @if (contactsOnly) {
                        <div>
                            <span class="c-margin-right--half ngInlineBlock ngBold">Add/Remove Contacts</span>
                            <p class="ngFinePrint">
                                Add or remove contacts from existing notification policy without changing timeline or
                                overall approval type.
                            </p>
                            <s25-toggle-button
                                (valueChange)="onContactToggleChange($event)"
                                [value]="addRemoveContact"
                                [trueLabel]="'Remove'"
                                [falseLabel]="'Add'"
                            ></s25-toggle-button>
                            @if (contactToggle === "Add") {
                                <div>
                                    <s25-ng-notification-policy
                                        [readOnly]="false"
                                        [showRemove]="false"
                                        [layout]="policyLayout"
                                        [fetchItemTypeId]="policyFetchItemTypeId"
                                        [fetchItemId]="policyFetchItemId"
                                        [fetchGeneration]="policyFetchGeneration"
                                        [initialDraft]="policyInitialDraft"
                                        [seededAsNew]="policySeededAsNew"
                                        [seedNonce]="policySeedNonce"
                                        class="notification-policy--wrapper"
                                    ></s25-ng-notification-policy>
                                </div>
                            }
                            @if (contactToggle === "Remove") {
                                <div>
                                    <s25-ng-multiselect-search-criteria
                                        [type]="'contacts'"
                                        [(modelBean)]="contList"
                                        [honorMatching]="true"
                                        class="removeContacts"
                                    ></s25-ng-multiselect-search-criteria>
                                </div>
                            }
                        </div>
                    } @else {
                        <div>
                            @if (contactsOnly !== null && contactsOnly !== undefined) {
                                <div>
                                    <span class="ngBold">Select Template</span>
                                    @if (itemTypeId === eventTypeNotifyId) {
                                        <s25-generic-dropdown
                                            [items]="chosenModels"
                                            [(value)]="chosen"
                                            [searchEnabled]="true"
                                            (valueChange)="chosenTemplate()"
                                        />
                                    } @else {
                                        <s25-ng-dropdown-search-criteria
                                            [type]="type"
                                            [(value)]="chosen"
                                            (valueChange)="chosenTemplate()"
                                        />
                                    }
                                </div>
                            }
                            <s25-ng-notification-policy
                                [readOnly]="false"
                                [showRemove]="false"
                                [layout]="policyLayout"
                                [fetchItemTypeId]="policyFetchItemTypeId"
                                [fetchItemId]="policyFetchItemId"
                                [fetchGeneration]="policyFetchGeneration"
                                [initialDraft]="policyInitialDraft"
                                [seededAsNew]="policySeededAsNew"
                                [seedNonce]="policySeedNonce"
                            ></s25-ng-notification-policy>
                        </div>
                    }
                </div>
            }
            <s25-ng-bulk-edit-save [submitFn]="updateFn" [buttonText]="buttonText"></s25-ng-bulk-edit-save>
            @if (successMsg && successMsg !== "") {
                <div
                    class="ngGreen ngBold cn-alert cn-alert--success c-margin-bottom--single c-margin-top--single"
                    role="alert"
                >
                    <div class="cn-alert__icon cn-icon" name="alert--info">
                        <s25-ng-icon [type]="'check'" [ariaLabel]="false" />
                    </div>
                    <div class="cn-alert__label">
                        <span>{{ successMsg }}</span>
                    </div>
                </div>
            }
        </div>
    }`,
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class S25BulkEditNotifyPolicyComponent implements OnInit {
    @Input() itemTypeId: number = undefined;
    @Input() itemIds: number[];
    // @Input() updateType?: 'template'|'contact'|'new';
    @Input() contactsOnly?: boolean;
    @Input() chosenModels?: any = [];
    @Input() itemTypeName?: any;

    protected readonly eventTypeNotifyId = NotificationPolicyModel.NotifyItemTypeId.eventType;
    readonly policyEditor = viewChild(S25NotificationPolicyComponent);
    policyLayout: NotificationPolicyModel.LayoutConfig = NotificationPolicyModel.defaultLayout();
    policyFetchItemTypeId: NotificationPolicyModel.NotifyFetchItemTypeId | undefined;
    policyFetchItemId: number | undefined;
    policyFetchGeneration = 0;
    policyInitialDraft: NotificationPolicyModel.FormModel | undefined;
    policySeededAsNew = true;
    policySeedNonce = 0;

    type: string;
    updating = false;
    message: string | null = null;
    chosen: DropDownItem | null = null;
    items: DropDownItem[];
    init = false;
    contList: any = { selectedItems: [], showResult: false, showMatching: false };
    contactToggle: "Add" | "Remove" | null = null;

    get addRemoveContact(): boolean {
        return this.contactToggle === "Remove";
    }
    policy: any;
    buttonText: string = "Update Notification Policy";
    msg: string = "";
    notifyTypeOnly: boolean = false;

    notifyTypes = [...Task.NotificationPolicy.ApproverQuorumOptions];

    selectedNotifyType: number;

    chunkSize = 500;
    concurrentMax = 5;
    chunkCount = 0;
    chunkIdx = 0;
    successMsg: any;

    constructor(
        private elementRef: ElementRef,
        private cd: ChangeDetectorRef,
    ) {
        this.elementRef.nativeElement.angBridge = this; //bridge to AngularJS; used for AngJS to set model values and call setter fns
    }

    ngOnInit(): void {
        this.chunkCount = Math.ceil(this.itemIds.length / this.chunkSize);
        this.type =
            S25Const.itemId2Display[this.itemTypeId] &&
            S25Const.itemId2Display[this.itemTypeId].plural &&
            S25Const.itemId2Display[this.itemTypeId].plural.toLowerCase();
        this.init = true;
        this.cd.detectChanges();
    }

    setNotifyType(e: any) {
        this.selectedNotifyType = e.itemId;
    }

    onNotifyTypeChange(event: any) {
        this.notifyTypeOnly = event;
        this.reset();
        this.cd.detectChanges();
    }

    chosenTemplate() {
        const templateId = S25Util.parseInt(this.chosen?.itemId);
        if (templateId != null) {
            this.policyInitialDraft = undefined;
            this.policyFetchItemTypeId = this.itemTypeId as NotificationPolicyModel.NotifyFetchItemTypeId;
            this.policyFetchItemId = templateId;
        } else {
            this.policyFetchItemTypeId = undefined;
            this.policyFetchItemId = undefined;
            this.policyInitialDraft = NotificationPolicyMapper.emptyDraft();
            this.policySeededAsNew = true;
        }
        this.cd.detectChanges();
        S25BulkEditSaveApi.reset(this.elementRef.nativeElement);
        this.cd.detectChanges();
    }

    onTypeChange(event: any) {
        this.reset();
        this.contactsOnly = !!event;
        this.policyLayout = this.contactsOnly
            ? { showApprovalWindow: false, showQuorum: false }
            : { showApprovalWindow: true, showQuorum: true };
        this.cd.detectChanges();
    }

    onContactToggleChange(event: any) {
        this.reset();
        this.contactToggle = event ? "Remove" : "Add";
        this.policyLayout = { showApprovalWindow: false, showQuorum: false };
        if (this.contactToggle === "Add") {
            this.policyInitialDraft = NotificationPolicyMapper.emptyDraftWithDefaults();
            this.policySeededAsNew = true;
        }
        this.cd.detectChanges();
    }

    reset() {
        this.chosen = null;
        this.contactToggle = null;
        this.message = null;
        this.contList = { contacts: [], showResult: true, showMatching: false };
        this.policyFetchItemTypeId = undefined;
        this.policyFetchItemId = undefined;
        this.policyFetchGeneration = 0;
        this.policyInitialDraft = undefined;
        this.policySeededAsNew = true;
        this.policyLayout = NotificationPolicyModel.defaultLayout();
        this.policySeedNonce++;
        S25BulkEditSaveApi.reset(this.elementRef.nativeElement);
    }

    get updateFn() {
        return this.update.bind(this);
    }

    private bulkNotifyItemTypeId(): NotificationPolicyModel.NotifyFetchItemTypeId {
        return this.itemTypeId as NotificationPolicyModel.NotifyFetchItemTypeId;
    }

    update() {
        this.successMsg = "";
        this.cd.detectChanges();
        if (this.notifyTypeOnly) {
            return NotificationPolicyService.updateNotifyTypeOnly(
                this.bulkNotifyItemTypeId(),
                this.itemIds,
                1,
                this.selectedNotifyType,
            );
        }

        const policyDraft = this.policyDraft();
        const eventType = this.itemTypeId === this.eventTypeNotifyId;

        if (this.contactsOnly) {
            if (this.contactToggle === null) {
                this.msg = "Please toggle to add or remove contacts.";
                alert(this.msg);
                return false;
            }
            const selectedCount =
                this.contactToggle === "Add"
                    ? policyDraft.assignments.length
                    : (this.contList.selectedItems?.length ?? 0);
            if (selectedCount === 0) {
                this.msg = "Please select a contact.";
                alert(this.msg);
                return false;
            }
            if (eventType) {
                return this.saveEventTypeContactChange(policyDraft);
            }
            return this.saveContacts();
        }

        if (NotificationPolicyMapper.isRemovedPolicy(policyDraft)) {
            if (eventType) {
                return NotificationPolicyService.updateEventTypeBulkEdit(
                    NotificationPolicyMapper.buildEventTypeBulkRemovePayload(this.itemIds),
                );
            }
            return this.delete();
        }

        if (!policyDraft.assignments.length) {
            if (eventType) {
                this.msg = "Please add a contact to this policy.";
                alert(this.msg);
                return false;
            }
            return this.delete();
        }

        if (eventType) {
            const payload = NotificationPolicyMapper.buildEventTypeBulkReplacePayload(this.itemIds, policyDraft);
            if (!payload) {
                return false;
            }
            return NotificationPolicyService.updateEventTypeBulkEdit(payload);
        }
        const editor = this.policyEditor();
        if (!editor?.validate()) {
            return false;
        }
        return NotificationPolicyService.replaceObjectsBulk(
            this.bulkNotifyItemTypeId(),
            this.itemIds,
            editor.getSession().draft,
        );
    }

    private policyDraft(): NotificationPolicyModel.FormModel {
        return this.policyEditor()?.getSession().draft ?? NotificationPolicyMapper.emptyDraft();
    }

    saveContacts() {
        const itemTypeId = this.bulkNotifyItemTypeId();
        if (this.contactToggle === "Add") {
            return NotificationPolicyService.addObjectBulkContacts(
                itemTypeId,
                this.itemIds,
                this.policyDraft().assignments,
            );
        }
        return NotificationPolicyService.removeObjectBulkContacts(itemTypeId, this.itemIds, this.selectedContactIds());
    }

    private saveEventTypeContactChange(policyDraft: NotificationPolicyModel.FormModel) {
        return NotificationPolicyService.updateEventTypeBulkEdit(
            NotificationPolicyMapper.buildEventTypeBulkContactChangePayload({
                itemIds: this.itemIds,
                mode: this.contactToggle === "Remove" ? "remove" : "add",
                assignments: this.contactToggle === "Add" ? policyDraft.assignments : undefined,
                contactIds: this.contactToggle === "Remove" ? this.selectedContactIds() : undefined,
            }),
        );
    }

    private selectedContactIds(): number[] {
        const selected = this.contList.selectedItems ?? [];
        return selected.flatMap((item: { itemId?: string | number }) => {
            const contactId = S25Util.parseInt(item.itemId);
            return contactId == null ? [] : [contactId];
        });
    }

    onLoad() {
        this.cd.detectChanges();
    }

    delete() {
        const batchHandler: any = (chunkIdx: any) => {
            this.chunkIdx++;
            const start = chunkIdx * this.chunkSize; //0,         chunkSize,     2 * chunkSize...
            const end = start + this.chunkSize; //     chunkSize, 2 * chunkSize, 3 * chunkSize...
            const chunk = this.itemIds.slice(start, end);
            return NotificationPolicyService.deleteBulk(this.bulkNotifyItemTypeId(), chunk);
        };

        S25Util.batch(batchHandler, this.concurrentMax, this.chunkCount).then(
            (resp) => {
                const checkSuccess = resp.find((i: any) => i.root === "");
                checkSuccess ? (this.successMsg = "Success!") : "";
                this.cd.detectChanges();
            },
            (error) => {
                this.done(error);
            },
        );
    }

    done(error?: any) {
        error && S25Util.showError(error);
    }
}
